/**
 * Currency formatting and conversion utilities.
 *
 * Supports VES (Bolívares), USD ($), EUR (€), COP (COL$), and other currencies
 * with locale-aware number formatting and exact 2-decimal financial rounding.
 */

// ─── Symbol & Locale maps ─────────────────────────────────────────────
const CURRENCY_SYMBOLS: Record<string, string> = {
  VES: 'Bs.S',
  USD: '$',
  EUR: '€',
  COP: 'COL$',
};

const CURRENCY_LOCALES: Record<string, string> = {
  VES: 'es-VE',
  USD: 'en-US',
  EUR: 'es-ES',
  COP: 'es-CO',
};

// ─── Cached formatters for performance ───────────────────────────────
const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(currency: string, locale?: string): Intl.NumberFormat {
  const targetLocale = locale || CURRENCY_LOCALES[currency.toUpperCase()] || 'es-VE';
  const key = `${targetLocale}:${currency}`;
  let fmt = formatterCache.get(key);

  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat(targetLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch {
      fmt = new Intl.NumberFormat('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    formatterCache.set(key, fmt);
  }
  return fmt;
}

// ─── Public API ──────────────────────────────────────────────────────

/**
 * Format an amount with the proper currency symbol and locale-aware number formatting.
 *
 * @example formatCurrency(12500.5, 'VES') → "Bs.S 12.500,50"
 * @example formatCurrency(100.5, 'USD')   → "$ 100.50"
 * @example formatCurrency(100.5, 'EUR')   → "€ 100,50"
 */
export function formatCurrency(amount: number | null | undefined, currency: string = 'USD'): string {
  const value = amount ?? 0;
  const currUpper = (currency || 'USD').toUpperCase();
  const symbol = CURRENCY_SYMBOLS[currUpper] || currUpper;
  const numFmt = getFormatter(currUpper);

  return `${symbol} ${numFmt.format(value)}`;
}

/**
 * Shorthand formatter for the local currency (VES / Bolívares).
 *
 * @example formatLocal(45000) → "Bs.S 45.000,00"
 */
export function formatLocal(amount: number | null | undefined): string {
  return formatCurrency(amount, 'VES');
}

/**
 * Format an amount in the configured base (foreign) currency.
 *
 * @example formatBase(125.50, 'USD') → "$ 125.50"
 */
export function formatBase(amount: number | null | undefined, baseCurrency: string = 'USD'): string {
  return formatCurrency(amount, baseCurrency);
}

/**
 * Convert a local-currency amount (VES) to foreign base currency (USD/EUR)
 * using the given exchange rate.
 *
 * Returns 0 when rate is missing or zero. Rounds cleanly to 2 decimals.
 *
 * @example toEquivalent(36_000, 36) → 1000
 */
export function toEquivalent(localAmount: number, rate: number): number {
  if (!rate || rate === 0) return 0;
  const raw = localAmount / rate;
  return Math.round((raw + Number.EPSILON) * 100) / 100;
}

/**
 * Convert a foreign base-currency amount (USD/EUR) to local currency (VES)
 * using the given exchange rate.
 *
 * Returns 0 when rate is missing or zero. Rounds cleanly to 2 decimals.
 *
 * @example toLocal(100, 36) → 3600
 */
export function toLocal(currencyAmount: number, rate: number): number {
  if (!rate || rate === 0) return 0;
  const raw = currencyAmount * rate;
  return Math.round((raw + Number.EPSILON) * 100) / 100;
}
